import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import FloatingContact from '../../components/feature/FloatingContact';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
}

const blogPosts: BlogPost[] = [
  {
    slug: 'minimalist-mimari-tasarim-ilkeleri',
    title: 'Minimalist Mimari Tasarım İlkeleri',
    excerpt: 'Minimalizm, mimarlıkta sadece bir estetik tercih değil, aynı zamanda mekânın özüne inme çabasıdır. Az ile çoğu ifade etmenin yollarını keşfedin.',
    date: '15 Ocak 2025',
    readTime: '8 dk',
    category: 'Tasarım Felsefesi',
    image: 'https://public.readdy.ai/ai/img_res/6994dba107227b8326a0679e910e41c3.jpg'
  },
  {
    slug: 'dogal-isik-mimaride-mekan-algisi',
    title: 'Doğal Işık ve Mimarlıkta Mekân Algısı',
    excerpt: 'Işık, mimarlığın en temel malzemesidir. Doğal ışığın mekân algısını nasıl dönüştürdüğünü ve tasarımda nasıl kullanıldığını inceleyin.',
    date: '12 Ocak 2025',
    readTime: '10 dk',
    category: 'Mekân Tasarımı',
    image: 'https://readdy.ai/api/search-image?query=natural%20light%20streaming%20through%20large%20windows%20in%20modern%20architectural%20space%20shadows%20and%20light%20play%20minimalist%20interior%20bright%20airy%20atmosphere%20professional%20photography&width=800&height=500&seq=blog2&orientation=landscape'
  },
  {
    slug: 'surdurulebilir-mimari-gelecek',
    title: 'Sürdürülebilir Mimari ve Geleceğin Yapıları',
    excerpt: 'Çevre duyarlı tasarım yaklaşımları ve sürdürülebilir malzeme kullanımı, modern mimarlığın vazgeçilmez unsurları haline geldi.',
    date: '8 Ocak 2025',
    readTime: '12 dk',
    category: 'Sürdürülebilirlik',
    image: 'https://readdy.ai/api/search-image?query=sustainable%20green%20architecture%20building%20with%20natural%20materials%20eco-friendly%20design%20modern%20structure%20integrated%20with%20nature%20environmental%20architecture%20professional%20photography&width=800&height=500&seq=blog3&orientation=landscape'
  },
  {
    slug: 'modern-konut-tasariminda-fonksiyon',
    title: 'Modern Konut Tasarımında Fonksiyon ve Estetik',
    excerpt: 'Çağdaş yaşam alanlarında fonksiyonellik ile estetiğin dengesi nasıl kurulur? Günlük yaşamı kolaylaştıran tasarım çözümlerini keşfedin.',
    date: '5 Ocak 2025',
    readTime: '9 dk',
    category: 'Konut Tasarımı',
    image: 'https://readdy.ai/api/search-image?query=modern%20residential%20interior%20design%20functional%20living%20space%20contemporary%20furniture%20clean%20aesthetic%20comfortable%20home%20architecture%20professional%20photography&width=800&height=500&seq=blog4&orientation=landscape'
  }
];

export default function Blog() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Blog | Levent Atalay - Mimarlık Yazıları ve Düşünceler';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Mimarlık, tasarım felsefesi ve mekân üzerine yazılar. Levent Atalay\'ın mimari yaklaşımı, tasarım ilkeleri ve sektörel görüşleri.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <FloatingContact />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6">
            Mimarlık Üzerine Düşünceler
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            Tasarım felsefesi, mekân algısı ve mimari yaklaşımlar üzerine yazılar
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {blogPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group cursor-pointer"
              >
                <article className="h-full flex flex-col">
                  {/* Image */}
                  <div className="relative w-full h-64 md:h-80 mb-6 overflow-hidden bg-gray-100">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 flex flex-col">
                    {/* Meta */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="text-gray-900 font-medium">{post.category}</span>
                      <span>•</span>
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>

                    {/* Title */}
                    <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mb-4 group-hover:text-gray-600 transition-colors">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-gray-600 leading-relaxed mb-6 flex-1">
                      {post.excerpt}
                    </p>

                    {/* Read More */}
                    <div className="flex items-center gap-2 text-gray-900 font-medium group-hover:gap-4 transition-all whitespace-nowrap">
                      <span>Devamını Oku</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}